class TPRangeSlider extends HTMLElement {
  static selectors = {
    rangeContainer: '.tp-range-slider__container',
    rangeSlider: '.tp-range-slider__slider',
    rangeThumbMin: '.tp-range-slider__thumb--min',
    rangeThumbMax: '.tp-range-slider__thumb--max',
    rangeInputMin: '.tp-range-slider__input--min',
    rangeInputMax: '.tp-range-slider__input--max',
    rangeTrackBg: '.tp-range-slider__track-bg',
    rangeTrackFill: '.tp-range-slider__track-fill',
    rangeValue: '.tp-range-slider__value'
  };

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeSlider();
  }

  initializeSlider() {
    const container = this.querySelector(TPRangeSlider.selectors.rangeContainer);
    if (!container) return;
    this.setupCustomSlider(container);
    this.updateRangeDisplay(container);
  }

  setupCustomSlider(container) {
    const slider = container.querySelector(TPRangeSlider.selectors.rangeSlider);
    const minThumb = container.querySelector(TPRangeSlider.selectors.rangeThumbMin);
    const maxThumb = container.querySelector(TPRangeSlider.selectors.rangeThumbMax);
    const minInput = container.querySelector(TPRangeSlider.selectors.rangeInputMin);
    const maxInput = container.querySelector(TPRangeSlider.selectors.rangeInputMax);

    console.log('Initializing range slider with container:', container);

    if (!slider || !minThumb || !maxThumb || !minInput || !maxInput) return;

    let min = parseInt(container.dataset.rangeMin);
    let max = parseInt(container.dataset.rangeMax);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      min = parseInt(minInput.value);
      max = parseInt(maxInput.value);
    }
    if (max <= min) return;
    let activeThumb = null;
    let startX = 0;
    let startValue = 0;
    let sliderRect = null;

    const getValueFromPosition = (x) => {
      const rect = slider.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
      const value = Math.round(min + percent * (max - min));
      return Math.max(min, Math.min(max, value));
    };

    const handleStart = (e, thumb) => {
      activeThumb = thumb;
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      startValue = parseInt(thumb === minThumb ? minInput.value : maxInput.value);
      sliderRect = slider.getBoundingClientRect();
      thumb.style.transition = 'none';
      thumb.style.zIndex = '3';
      if (thumb === minThumb) {
        maxThumb.style.zIndex = '2';
      } else {
        minThumb.style.zIndex = '2';
      }
      e.preventDefault();
    };

    const handleMove = (e) => {
      if (!activeThumb) return;
      const currentX = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaX = currentX - startX;
      const rect = sliderRect || slider.getBoundingClientRect();
      const deltaPercent = deltaX / rect.width;
      const deltaValue = Math.round(deltaPercent * (max - min));
      let newValue = startValue + deltaValue;
      newValue = Math.max(min, Math.min(max, newValue));
      if (activeThumb === minThumb) {
        const maxValue = parseInt(maxInput.value);
        newValue = Math.min(newValue, maxValue);
        minInput.value = newValue;
      } else {
        const minValue = parseInt(minInput.value);
        newValue = Math.max(newValue, minValue);
        maxInput.value = newValue;
      }
      this.updateRangeDisplay(container);
    };

    const handleEnd = () => {
      if (activeThumb) {
        activeThumb.style.transition = '';
        activeThumb.style.zIndex = '2';
        activeThumb = null;
        sliderRect = null;
      }
    };

    minThumb.addEventListener('mousedown', (e) => handleStart(e, minThumb));
    maxThumb.addEventListener('mousedown', (e) => handleStart(e, maxThumb));
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    minThumb.addEventListener('touchstart', (e) => handleStart(e, minThumb), { passive: false });
    maxThumb.addEventListener('touchstart', (e) => handleStart(e, maxThumb), { passive: false });
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    slider.addEventListener('click', (e) => {
      if (e.target === slider || e.target.classList.contains(TPRangeSlider.selectors.rangeTrackBg.slice(1)) || e.target.classList.contains(TPRangeSlider.selectors.rangeTrackFill.slice(1))) {
        const newValue = getValueFromPosition(e.clientX);
        const minValue = parseInt(minInput.value);
        const maxValue = parseInt(maxInput.value);
        const distToMin = Math.abs(newValue - minValue);
        const distToMax = Math.abs(newValue - maxValue);
        if (distToMin < distToMax) {
          minInput.value = newValue;
        } else {
          maxInput.value = newValue;
        }
        this.updateRangeDisplay(container);
      }
    });
  }

  updateRangeDisplay(container) {
    const minInput = container.querySelector(TPRangeSlider.selectors.rangeInputMin);
    const maxInput = container.querySelector(TPRangeSlider.selectors.rangeInputMax);
    const minThumb = container.querySelector(TPRangeSlider.selectors.rangeThumbMin);
    const maxThumb = container.querySelector(TPRangeSlider.selectors.rangeThumbMax);
    const minValueDisplay = minThumb?.querySelector(TPRangeSlider.selectors.rangeValue);
    const maxValueDisplay = maxThumb?.querySelector(TPRangeSlider.selectors.rangeValue);
    const trackFill = container.querySelector(TPRangeSlider.selectors.rangeTrackFill);
    const slider = container.querySelector(TPRangeSlider.selectors.rangeSlider);
    if (!minInput || !maxInput || !slider) return;
    const minValue = parseInt(minInput.value);
    const maxValue = parseInt(maxInput.value);
    let min = parseInt(container.dataset.rangeMin);
    let max = parseInt(container.dataset.rangeMax);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      min = parseInt(minInput.value);
      max = parseInt(maxInput.value);
    }
    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return;
    const range = max - min;
    if (range <= 0) return;
    if (minValueDisplay) {
      minValueDisplay.textContent = minValue;
      minThumb.setAttribute('aria-valuenow', minValue);
    }
    if (maxValueDisplay) {
      maxValueDisplay.textContent = maxValue;
      maxThumb.setAttribute('aria-valuenow', maxValue);
    }
    const sliderWidth = slider.offsetWidth;
    const minPercent = ((minValue - min) / range) * 100;
    const maxPercent = ((maxValue - min) / range) * 100;
    if (minThumb) {
      minThumb.style.left = `${minPercent}%`;
    }
    if (maxThumb) {
      maxThumb.style.left = `${maxPercent}%`;
    }
    if (trackFill) {
      trackFill.style.left = `${minPercent}%`;
      trackFill.style.width = `${maxPercent - minPercent}%`;
    }
    
    // Update checkboxes based on inclusive range
    this.updateCheckboxes(minValue, maxValue);
  }

  updateCheckboxes(minValue, maxValue) {
    // Find all checkboxes in the filter-body
    const filterBody = this.querySelector('.filter-body');
    if (!filterBody) return;

    const checkboxes = filterBody.querySelectorAll('input[type="checkbox"]');
    let hasChanges = false;
    
    checkboxes.forEach(checkbox => {
      // Parse the numeric value from the checkbox value/label
      const checkboxValue = parseInt(checkbox.value);

      console.log(`Checking checkbox with value: ${checkboxValue} against range: ${minValue} - ${maxValue}`);
      
      // Check if the value is a valid number and within the inclusive range
      if (Number.isFinite(checkboxValue)) {
        const shouldBeChecked = checkboxValue >= minValue && checkboxValue <= maxValue;
        
        // Only trigger click if the state needs to change
        if (shouldBeChecked && !checkbox.disabled && !checkbox.checked) {
          hasChanges = true;
          // Click will toggle the checkbox AND trigger any event listeners
          checkbox.click();
        } else if (!shouldBeChecked && checkbox.checked) {
          hasChanges = true;
          // Click will toggle the checkbox AND trigger any event listeners
          checkbox.click();
        }
      }
    });
  }
}

customElements.define('tp-range-slider', TPRangeSlider);
